import { env } from "@/api/env"
import { AppError } from "@/api/problem"
import type { Session } from "@/api/generated/model"
import { withRefreshLease } from "./refresh-lease"
import { onSessionMessage, postSessionMessage } from "./session-channel"
import { onSessionLost } from "./session-lost"
import { markSessionHint } from "./session-hint"
import { tokenStore } from "./token-store"

/**
 * Concurrent 401s make exactly ONE refresh (LLD §6.2, FR-DSH-SES-02,
 * Review Focus #3). The failure this prevents: five queries meet one expired
 * token and send five refreshes; four present a cookie the first already
 * rotated, the backend takes that for a stolen copy and kills the family.
 */
let inFlight: Promise<boolean> | null = null

/** A token another tab obtained, kept for a tab that was waiting on the lock. */
let lastShared: { token: string; at: number } | null = null
const ADOPT_WITHIN_MS = 10_000
/** A refresh that has not answered by then is a transport failure (fix round 1). */
const REFRESH_TIMEOUT_MS = 10_000

onSessionMessage((m) => {
  if (m.kind === "token") lastShared = { token: m.token, at: m.at }
  if (m.kind === "signout") {
    // The signed-out session's token must not be adopted by a later one.
    lastShared = null
    // Another tab signed out: follow it, without calling logout or echoing it.
    if (tokenStore.get() !== null) {
      onSessionLost({ hadToken: true, broadcast: false, ...(m.reason && { reason: m.reason }) })
    }
  }
})

/** N callers in this tab → one network refresh; the `finally` lets a later expiry refresh again. */
export function refreshOnce(): Promise<boolean> {
  inFlight ??= withCrossTabLock(doRefresh).finally(() => {
    inFlight = null
  })
  return inFlight
}

/**
 * Across tabs: refreshes never overlap (Web Locks, or the storage lease where
 * the browser has none, e.g. a LAN deployment over plain http). A tab that
 * waited adopts a token another tab broadcast in the last 10 s instead of
 * refreshing again, unless that is the very token it already holds (the one
 * that just answered 401). A shared token is adopted once.
 */
async function withCrossTabLock(fn: () => Promise<boolean>): Promise<boolean> {
  const tokenAtCall = tokenStore.get()
  const underLock = async (): Promise<boolean> => {
    const shared = lastShared
    if (shared && shared.token !== tokenAtCall && Date.now() - shared.at < ADOPT_WITHIN_MS) {
      lastShared = null
      tokenStore.set(shared.token)
      markSessionHint()
      return true
    }
    return fn()
  }
  if ("locks" in navigator) return navigator.locks.request("devsms.refresh", underLock)
  return withRefreshLease(underLock)
}

/** The refusals that end a session; anything else says nothing about the cookie. */
const isRefusal = (status: number) => status === 401 || status === 403

/** Ported from akav-eli's doRefresh (src/auth/refresh.ts), keeping the problem code. */
async function doRefresh(): Promise<boolean> {
  // Captured BEFORE the attempt. A refused refresh while a token existed ends a
  // session: worth the disruptive exit. With no token it is the anonymous
  // visitor's "is there a cookie?" check, and a refusal is the ordinary case.
  const tokenAtStart = tokenStore.get()
  const hadToken = tokenAtStart !== null
  // Stale-login guard: a sign-in (or a sign-out) may land while this attempt
  // is out, typically the boot check raced by a login a moment later. If the
  // store changed, this attempt must not touch it.
  const stale = (): boolean => tokenStore.get() !== tokenAtStart
  try {
    // A bare fetch: the mutator would re-enter its own 401 path.
    const res = await fetch(`${env.VITE_API_BASE_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json, application/problem+json" },
      signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
    })
    if (stale()) return false
    if (!res.ok) {
      // A 5xx or a 429 is not an answer about the session: the backend keeps
      // the cookie on a 5xx on purpose, so this tab keeps its token too.
      if (!isRefusal(res.status)) return false
      const err = await AppError.fromResponse(res) // refresh_reused, session_invalid, account_disabled…
      onSessionLost({ hadToken, code: err.code })
      return false
    }
    const s = (await res.json()) as Partial<Session>
    if (stale()) return false
    if (!s.accessToken) {
      onSessionLost({ hadToken, code: "session_invalid" })
      return false
    }
    tokenStore.set(s.accessToken)
    markSessionHint()
    postSessionMessage({ kind: "token", token: s.accessToken, at: Date.now() })
    return true
  } catch {
    // Offline, or the 10 s timeout: the session may be fine. Nothing is
    // cleared; the caller's 401 surfaces and the next request tries again.
    return false
  }
}

/** Test seam. */
export function __resetRefreshState(): void {
  inFlight = null
  lastShared = null
}
